/**
 * Experience Examples Data
 *
 * SRD-sourced examples organized by category to inspire player-created experiences.
 * Each experience provides a +2 bonus when applicable to the character's actions.
 *
 * Categories:
 * - Backgrounds: Past professions or roles
 * - Characteristics: Personality traits and social qualities
 * - Specialties: Unique skills or expertise areas
 * - Skills: Practical abilities and talents
 * - Phrases: Character mottos or guiding principles
 */

export interface ExperienceExample {
  name: string;
  description?: string; // Optional flavor text to help players understand the example
}

export interface ExperienceCategoryData {
  name: string;
  description: string;
  examples: ExperienceExample[];
}

export const EXPERIENCE_CATEGORIES: Record<string, ExperienceCategoryData> = {
  backgrounds: {
    name: 'Backgrounds',
    description: 'Past professions, roles, or life experiences that shaped your character',
    examples: [
      { name: 'Assassin', description: 'Former hired killer with knowledge of poisons and stealth' },
      { name: 'Blacksmith', description: 'Skilled metalworker familiar with weapons and armor' },
      { name: 'Bodyguard', description: 'Professional protector trained in defensive tactics' },
      { name: 'Bounty Hunter', description: 'Tracker and captor of fugitives and criminals' },
      { name: 'Chef to the Royal Family', description: 'Elite culinary expert with court connections' },
      { name: 'Circus Performer', description: 'Acrobat, entertainer, or showperson with crowd skills' },
      { name: 'Con Artist', description: 'Deceptive schemer skilled in manipulation and disguise' },
      { name: 'Fallen Monarch', description: 'Former ruler with leadership experience and political knowledge' },
      { name: 'Field Medic', description: 'Combat healer experienced in emergency treatment' },
      { name: 'High Priestess', description: 'Religious leader with ceremonial and spiritual knowledge' },
      { name: 'Merchant', description: 'Trader skilled in negotiation and market knowledge' },
      { name: 'Noble', description: 'Aristocrat familiar with etiquette, politics, and high society' },
      { name: 'Pirate', description: 'Seafarer experienced in naval combat and navigation' },
      { name: 'Politician', description: 'Diplomat skilled in rhetoric, compromise, and governance' },
      { name: 'Runaway', description: 'Fugitive experienced in hiding, survival, and evasion' },
      { name: 'Scholar', description: 'Academic expert in research, languages, or history' },
      { name: 'Sellsword', description: 'Mercenary warrior skilled in various combat styles' },
      { name: 'Soldier', description: 'Military veteran trained in tactics and discipline' },
      { name: 'Storyteller', description: 'Bard or narrator skilled in performance and lore' },
      { name: 'Thief', description: 'Skilled in stealth, lockpicking, and urban survival' },
      { name: 'World Traveler', description: 'Explorer with knowledge of distant lands and cultures' },
    ],
  },
  characteristics: {
    name: 'Characteristics',
    description: 'Personality traits, social qualities, or behavioral tendencies',
    examples: [
      { name: 'Affable', description: 'Friendly and easy to approach, skilled at making others comfortable' },
      { name: 'Battle-Hardened', description: 'Experienced in combat with mental toughness and resilience' },
      { name: 'Bookworm', description: 'Voracious reader with extensive knowledge from study' },
      { name: 'Charming', description: 'Naturally likable with persuasive social skills' },
      { name: 'Cowardly', description: 'Survival-focused, skilled at avoiding danger and escaping threats' },
      { name: 'Friend to All', description: 'Universally friendly with ability to find common ground' },
      { name: 'Helpful', description: 'Naturally supportive and skilled at assisting others' },
      { name: 'Intimidating Presence', description: 'Naturally imposing, skilled at threats and dominance' },
      { name: 'Leader', description: 'Natural commander skilled at inspiring and directing others' },
      { name: 'Lone Wolf', description: 'Self-reliant individualist comfortable working alone' },
      { name: 'Loyal', description: 'Fiercely devoted to allies and commitments' },
      { name: 'Observant', description: 'Highly perceptive, noticing details others miss' },
      { name: 'Prankster', description: 'Mischievous trickster skilled at jokes and diversions' },
      { name: 'Silver Tongue', description: 'Eloquent speaker skilled in persuasion and deception' },
      { name: 'Sticky Fingers', description: 'Talented pickpocket with deft sleight of hand' },
      { name: 'Stubborn to a Fault', description: 'Unyielding determination that can aid or hinder' },
      { name: 'Survivor', description: 'Resilient and resourceful in desperate situations' },
      { name: 'Young and Naive', description: 'Inexperienced but optimistic and eager to learn' },
    ],
  },
  specialties: {
    name: 'Specialties',
    description: 'Unique areas of expertise or specialized training',
    examples: [
      { name: 'Acrobat', description: 'Expert in gymnastics, climbing, and physical feats' },
      { name: 'Gambler', description: 'Skilled at games of chance and reading opponents' },
      { name: 'Healer', description: 'Experienced in medicine, herbalism, and treating injuries' },
      { name: 'Inventor', description: 'Creative tinkerer skilled with devices and mechanisms' },
      { name: 'Magical Historian', description: 'Expert in arcane lore and magical traditions' },
      { name: 'Mapmaker', description: 'Skilled cartographer with navigation expertise' },
      { name: 'Master of Disguise', description: 'Expert at assuming false identities and appearances' },
      { name: 'Navigator', description: 'Expert in direction-finding and route planning' },
      { name: 'Sharpshooter', description: 'Exceptional marksman with ranged weapons' },
      { name: 'Survivalist', description: 'Expert in wilderness survival and resourcefulness' },
      { name: 'Swashbuckler', description: 'Daring combatant skilled in flashy, agile fighting' },
      { name: 'Tactician', description: 'Strategic thinker skilled in planning and analysis' },
    ],
  },
  skills: {
    name: 'Skills',
    description: 'Practical abilities and trained talents',
    examples: [
      { name: 'Animal Whisperer', description: 'Natural affinity for calming and understanding animals' },
      { name: 'Barter', description: 'Skilled negotiator in trade and economic exchange' },
      { name: 'Deadly Aim', description: 'Precise targeting with ranged attacks' },
      { name: 'Fast Learner', description: 'Quick to pick up new skills and knowledge' },
      { name: 'Incredible Strength', description: 'Exceptional physical power and lifting ability' },
      { name: 'Liar', description: 'Skilled at deception and maintaining false stories' },
      { name: 'Light Feet', description: 'Naturally quiet and graceful movement' },
      { name: 'Negotiator', description: 'Expert at finding peaceful solutions and compromise' },
      { name: 'Photographic Memory', description: 'Exceptional recall of visual details and information' },
      { name: 'Quick Hands', description: 'Lightning-fast manual dexterity' },
      { name: 'Repair', description: 'Skilled at fixing broken objects and equipment' },
      { name: 'Scavenger', description: 'Expert at finding useful items and resources' },
      { name: 'Tracker', description: 'Skilled at following trails and reading signs' },
    ],
  },
  phrases: {
    name: 'Phrases',
    description: 'Character mottos, guiding principles, or catchphrases',
    examples: [
      { name: 'Catch Me If You Can', description: 'Confidence in evasion and escape abilities' },
      { name: 'Fake It Till You Make It', description: 'Confidence through bluffing and improvisation' },
      { name: 'First Time\'s the Charm', description: 'Lucky or skilled at succeeding on initial attempts' },
      { name: 'Hold the Line', description: 'Determination to stand firm against threats' },
      { name: 'I Won\'t Let You Down', description: 'Reliability and commitment to promises' },
      { name: 'I\'ll Catch You', description: 'Protective instinct and quick reflexes' },
      { name: 'I\'ve Got Your Back', description: 'Loyalty and protective support for allies' },
      { name: 'Knowledge Is Power', description: 'Value placed on learning and information' },
      { name: 'Nature\'s Friend', description: 'Harmony with natural world and creatures' },
      { name: 'Never Again', description: 'Determination driven by past trauma or failure' },
      { name: 'No One Left Behind', description: 'Commitment to protecting all allies' },
      { name: 'Pick on Someone Your Own Size', description: 'Defender of the weak and vulnerable' },
      { name: 'The Show Must Go On', description: 'Perseverance despite obstacles or setbacks' },
      { name: 'This Is Not a Negotiation', description: 'Assertiveness and unwillingness to compromise' },
      { name: 'Wolf in Sheep\'s Clothing', description: 'Deceptive appearance hiding true capabilities' },
    ],
  },
};

/**
 * Get all category IDs
 */
export const getCategoryIds = (): string[] => {
  return Object.keys(EXPERIENCE_CATEGORIES);
};

/**
 * Get category data by ID
 */
export const getCategoryById = (categoryId: string): ExperienceCategoryData | undefined => {
  return EXPERIENCE_CATEGORIES[categoryId];
};

/**
 * Get random examples from a category
 */
export const getRandomExamples = (categoryId: string, count: number = 3): ExperienceExample[] => {
  const category = getCategoryById(categoryId);
  if (!category) return [];

  const shuffled = [...category.examples].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

/**
 * Get all examples from all categories (for search/filter functionality)
 */
export const getAllExamples = (): { category: string; example: ExperienceExample }[] => {
  const allExamples: { category: string; example: ExperienceExample }[] = [];

  Object.entries(EXPERIENCE_CATEGORIES).forEach(([categoryId, categoryData]) => {
    categoryData.examples.forEach(example => {
      allExamples.push({ category: categoryId, example });
    });
  });

  return allExamples;
};
